import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  subDays,
  format,
  workingDayAnchor,
  workingDayKey,
} from '@spcc/core'
import type { DateRange, DatePreset, ComparisonType } from '../types'

// Friday = day 5 (0=Sunday in JS)
const FRIDAY = 5

/** Step back 1 day; if that day is Friday, step back one more (→ Thursday). */
function skipFriday(d: Date): Date {
  return d.getDay() === FRIDAY ? subDays(d, 1) : d
}

/**
 * Returns the last non-Friday working day before today.
 * If yesterday is Friday, steps back to Thursday.
 * Used for DoD ("yesterday" preset), WoW, and MoM helpers.
 */
function lastWorkingDay(now: Date): Date {
  return skipFriday(subDays(startOfDay(now), 1))
}

/**
 * Given the current DoD/WoW/MoM date range, compute the previous-period
 * date range — with Friday always skipped.
 * Returns { prevFrom, prevTo } as "yyyy-MM-dd" strings.
 */
export function computeComparisonRange(
  currentFrom: string,
  currentTo: string,
  comparisonType: ComparisonType
): { prevFrom: string; prevTo: string } {
  const fmt = (d: Date) => format(d, 'yyyy-MM-dd')
  const parseLocal = (s: string) => startOfDay(new Date(s + 'T12:00:00')) // noon avoids DST edge

  switch (comparisonType) {
    case 'dod': {
      // Previous day = current day - 1; skip Friday
      const prev = skipFriday(subDays(parseLocal(currentFrom), 1))
      return { prevFrom: fmt(prev), prevTo: fmt(prev) }
    }
    case 'wow': {
      // Previous week = exactly 7 days before
      const prevFrom = subDays(parseLocal(currentFrom), 7)
      const prevTo = subDays(parseLocal(currentTo), 7)
      return { prevFrom: fmt(prevFrom), prevTo: fmt(prevTo) }
    }
    case 'mom': {
      // Previous month: same dates, one month back
      const pFrom = parseLocal(currentFrom)
      pFrom.setMonth(pFrom.getMonth() - 1)
      const pTo = parseLocal(currentTo)
      pTo.setMonth(pTo.getMonth() - 1)
      return { prevFrom: fmt(pFrom), prevTo: fmt(pTo) }
    }
  }
}

/**
 * Returns the most recent completed working week (Saturday–Thursday).
 * Work week is 6 days: Sat=0... wait, Sat=6 in JS. Thu=4, Fri=5, Sat=6.
 * Walk backwards from yesterday until we find the most recent Thursday (day 4),
 * then go back 5 more days to reach the preceding Saturday.
 */
function lastWorkingWeek(now: Date): { start: Date; end: Date } {
  let lastThursday = subDays(startOfDay(now), 1)
  while (lastThursday.getDay() !== 4) { // 4 = Thursday
    lastThursday = subDays(lastThursday, 1)
  }
  // Sat–Thu = 6 working days: go back 5 days from Thursday to reach Saturday
  const lastSaturday = subDays(lastThursday, 5)
  return { start: lastSaturday, end: lastThursday }
}

export function resolveDateRange(dateRange: DateRange): { from: string; to: string } {
  if (dateRange.preset === 'custom' && dateRange.from && dateRange.to) {
    return { from: dateRange.from, to: dateRange.to }
  }

  // Anchor on the Riyadh day (00:00 → 00:00 AST), not the viewer's wall-clock
  // day — that is the window the backend will query. Local noon of that day
  // keeps the date-fns math below (all whole-day operations) correct in any
  // viewer timezone.
  const now = new Date(`${workingDayKey(workingDayAnchor())}T12:00:00`)
  const fmt = (d: Date) => format(d, 'yyyy-MM-dd')

  switch (dateRange.preset) {
    case 'today':
      return { from: fmt(startOfDay(now)), to: fmt(endOfDay(now)) }
    case 'yesterday': {
      // DoD: last working day (Sat–Thu). Friday is a weekend, skip it.
      const d = lastWorkingDay(now)
      return { from: fmt(d), to: fmt(d) }
    }
    case 'this_week':
      return { from: fmt(startOfWeek(now, { weekStartsOn: 0 })), to: fmt(endOfWeek(now, { weekStartsOn: 0 })) }
    case 'last_week': {
      const { start, end } = lastWorkingWeek(now)
      return { from: fmt(start), to: fmt(end) }
    }
    case 'this_month':
      return { from: fmt(startOfMonth(now)), to: fmt(endOfMonth(now)) }
    case 'last_month': {
      const lastOfPrev = subDays(startOfMonth(now), 1)
      return { from: fmt(startOfMonth(lastOfPrev)), to: fmt(lastOfPrev) }
    }
    case 'this_quarter':
      return { from: fmt(startOfQuarter(now)), to: fmt(endOfQuarter(now)) }
    case 'this_year':
      return { from: fmt(startOfYear(now)), to: fmt(endOfYear(now)) }
    case 'last_30_days':
      return { from: fmt(subDays(now, 30)), to: fmt(now) }
    default:
      return { from: fmt(startOfMonth(now)), to: fmt(endOfMonth(now)) }
  }
}

export function getDateRangeFromPreset(preset: DatePreset): { from: string; to: string } {
  return resolveDateRange({ preset, from: null, to: null })
}

export function getComparisonLabel(comparisonType: ComparisonType): string {
  switch (comparisonType) {
    case 'dod': return 'Day over Day'
    case 'wow': return 'Week over Week'
    case 'mom': return 'Month over Month'
  }
}

/** Map i18n language code to a BCP-47 locale string for date/number formatting. */
export function getLang(lang: string): string {
  if (lang === 'ar') return 'ar-SA'
  if (lang === 'ur') return 'ur-PK'
  return 'en-US'
}

export function formatTons(value: number): string {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`
  }
  return value.toFixed(1)
}

export function formatPercent(value: number): string {
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`
}

/**
 * Resolve a potentially localized name field.
 * Strapi i18n returns { en: "...", ar: "..." } objects for localized fields.
 * Pass `locale` (e.g. 'ar', 'ur') to pick that language; falls back to 'en' then any string value.
 */
export function resolveName(value: unknown, locale?: string): string {
  if (typeof value === 'string') return value
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>
    // Try the requested locale first
    if (locale && typeof obj[locale] === 'string') return obj[locale] as string
    // Fallback: 'en' then first available string
    if (typeof obj.en === 'string') return obj.en
    for (const v of Object.values(obj)) {
      if (typeof v === 'string') return v
    }
  }
  return ''
}

export * from './export-sales-dashboard'
